import { createContext, useContext, useState, useCallback } from 'react';
import { flushSync } from 'react-dom';
import api from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });
  const [token, setToken] = useState(() => localStorage.getItem('token'));

  const login = useCallback(async (identifier, password) => {
    const { data } = await api.post('/auth/login', { identifier, password });
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    // flushSync ensures state is applied before navigate() runs in the caller
    flushSync(() => {
      setToken(data.token);
      setUser(data.user);
    });
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    // Call backend logout endpoint (optional - JWT is stateless)
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // Ignore errors - proceed with local cleanup
    }
    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Clear sessionStorage too
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    // Clear any other app-cached keys from both storages
    const keysToRemove = [];
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('wr_page_') || key.startsWith('wo_page_') || key.startsWith('savedFilters_') || 
          key.startsWith('lastBranch') || key.startsWith('kanban_') || key.startsWith('filter_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    // Clear sessionStorage cached keys
    for (const key of Object.keys(sessionStorage)) {
      sessionStorage.removeItem(key);
    }
    // Reset in-memory state
    setToken(null);
    setUser(null);
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => {
      const updated = { ...prev, ...updates };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, updateUser, isAuthenticated: !!token }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
